import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type MessageActionsBehaviour,
    ToggleGroup,
    CodeViewer,
    Message,
    Button,
    Bubble,
    Trace
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    HeartIcon,
    StarIcon,
    InfoIcon
} from "../../anatomy/icons";

const NAME = "Message";

const USAGE_CODE = `<Message
    avatar={{
        title: "Ada Lovelace"
    }}
    title="Ada Lovelace"
    timestamp={sentAt}
>
    <Bubble>
        Hi! How can I help today?
    </Bubble>
</Message>

<Message
    deliveryStatus="read"
    timestamp={sentAt}
    align="end"
>
    <Bubble
        variant="user"
    >
        Could you send the report?
    </Bubble>
</Message>`;

const GROUP_CODE = `<Message.Group
    avatar={{
        title: "Ada Lovelace"
    }}
    title="Ada Lovelace"
>
    <Message
        timestamp={sentAt}
    >
        <Message.Header
            badge="Support"
        />
        <Bubble>
            Hello, I received your request.
        </Bubble>
    </Message>
    <Message>
        <Bubble>
            I'm checking the invoice now.
        </Bubble>
    </Message>
</Message.Group>`;

const ACTIONS_CODE = `<Message
    actionsBehaviour="auto"
    title="Ada Lovelace"
    isLatest={false}
>
    <Bubble>
        Hi! How can I help today?
    </Bubble>
    <Message.Actions>
        <Button
            accessibilityLabel="React"
            onPress={handleReact}
            icon={HeartIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />
    </Message.Actions>
</Message>`;

const STATUS_FLOW = [
    "sending",
    "sent",
    "delivered",
    "read"
] as const;

const RETRY_FLOW = [
    "failed",
    "sending",
    "sent"
] as const;

const ROWS_CODE = `<Message
    avatar={{
        title: "Ada Lovelace"
    }}
    title="Ada Lovelace"
>
    <Message.Row>
        <Bubble>
            I ran the tests and two files fail. The fix is below.
        </Bubble>
    </Message.Row>
    <Message.Row
        isShowActions={false}
    >
        <CodeViewer
            language="typescript"
            code={fix}
        />
    </Message.Row>
    <Message.Row
        isShowActions={false}
    >
        <Trace>
            <Trace.Line
                title="Files read"
                isConnected={true}
            />
            <Trace.Line
                title="Running tests"
                status="running"
            />
        </Trace>
    </Message.Row>
</Message>

<Message>
    <Message.Row>
        <Bubble
            variant="danger"
        >
            This reply was blocked by the content rules.
        </Bubble>
    </Message.Row>
    <Message.Row
        isShowActions={false}
    >
        <Button
            onPress={handleRetry}
            title="Try again"
            variant="link"
            type="neutral"
            size="small"
        />
    </Message.Row>
</Message>`;

const ROWS_FIX = [
    "const total = invoices.reduce(",
    "    (sum, invoice) => sum + invoice.amount,",
    "    0",
    ");"
].join("\n");

const STATUSES = [
    "sending",
    "queued",
    "sent",
    "delivered",
    "read"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const BEHAVIOURS: Array<MessageActionsBehaviour> = [
    "auto",
    "always",
    "none"
];

const STATUS_DELAY = 900;

const RETRY_DELAY = 1200;

const BLOCKED_RETRY_DELAY = 1400;

const getTimestamp = (daysAgo: number, hours: number, minutes: number) => {
    const timestamp = new Date();

    timestamp.setDate(timestamp.getDate() - daysAgo);

    timestamp.setHours(hours, minutes, 0, 0);

    return timestamp;
};

const TODAY = getTimestamp(0, 14, 32);

const YESTERDAY = getTimestamp(1, 18, 5);

const EARLIER = getTimestamp(4, 9, 15);

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("messagePage.groupsTitle"),
        id: "groups"
    },
    {
        title: t("messagePage.kindsTitle"),
        id: "kinds"
    },
    {
        title: t("messagePage.rowsTitle"),
        id: "rows"
    },
    {
        title: t("messagePage.statusTitle"),
        id: "status"
    },
    {
        title: t("messagePage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MessagePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        statusIndex,
        setStatusIndex
    ] = useState(STATUS_FLOW.length - 1);

    const [
        retryIndex,
        setRetryIndex
    ] = useState(0);

    const [
        isBlockedRetrying,
        setIsBlockedRetrying
    ] = useState(false);

    const [
        behaviour,
        setBehaviour
    ] = useState<MessageActionsBehaviour>("auto");

    useEffect(() => {
        if(statusIndex >= STATUS_FLOW.length - 1) {
            return;
        }

        const timer = window.setTimeout(() => setStatusIndex(statusIndex + 1), STATUS_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        statusIndex
    ]);

    useEffect(() => {
        if(!isBlockedRetrying) {
            return;
        }

        const timer = window.setTimeout(() => setIsBlockedRetrying(false), BLOCKED_RETRY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isBlockedRetrying
    ]);

    useEffect(() => {
        if(retryIndex === 0) {
            return;
        }

        const timer = window.setTimeout(() => setRetryIndex(retryIndex === RETRY_FLOW.length - 1 ? 0 : retryIndex + 1), RETRY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        retryIndex
    ]);

    const handleSend = () => setStatusIndex(0);

    const handleRetry = () => setRetryIndex(1);

    const handleBlockedRetry = () => setIsBlockedRetrying(true);

    const handleBehaviourChange = (value: string | Array<string>) => {
        const next = BEHAVIOURS.find((item) => item === value);

        if(next) {
            setBehaviour(next);
        }
    };

    const renderActions = () => <Message.Actions>
        <Button
            accessibilityLabel={t("messagePage.actionReact")}
            onPress={() => undefined}
            icon={HeartIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />
        <Button
            accessibilityLabel={t("messagePage.actionSave")}
            onPress={() => undefined}
            icon={StarIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />
        <Button
            accessibilityLabel={t("messagePage.actionDetails")}
            onPress={() => undefined}
            icon={InfoIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />
    </Message.Actions>;

    return <Article>
        <ComponentHeader
            lead={t("messagePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("messagePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("messagePage.overviewUse")}
            </Paragraph>
            <Preview
                caption={t("messagePage.overviewCaption")}
                isStacked={true}
            >
                <Message
                    avatar={{
                        title: t("sample.name")
                    }}
                    title={t("sample.name")}
                    timestamp={TODAY}
                >
                    <Bubble>
                        {t("sample.message")}
                    </Bubble>
                </Message>
                <Message
                    deliveryStatus={STATUS_FLOW[statusIndex]}
                    timestamp={TODAY}
                    align="end"
                >
                    <Bubble
                        variant="user"
                    >
                        {t("sample.reply")}
                    </Bubble>
                </Message>
                <Button
                    isDisabled={statusIndex < STATUS_FLOW.length - 1}
                    title={t("messagePage.send")}
                    onPress={handleSend}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("messagePage.groupsTitle")}
            id="groups"
        >
            <Paragraph>
                {t("messagePage.groupsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Message.Group
                    avatar={{
                        title: t("sample.name")
                    }}
                    title={t("sample.name")}
                >
                    <Message
                        timestamp={TODAY}
                    >
                        <Message.Header
                            badge={t("messagePage.badge")}
                        />
                        <Bubble>
                            {t("messagePage.groupOne")}
                        </Bubble>
                    </Message>
                    <Message>
                        <Bubble>
                            {t("messagePage.groupTwo")}
                        </Bubble>
                    </Message>
                    <Message>
                        <Bubble>
                            {t("messagePage.groupThree")}
                        </Bubble>
                    </Message>
                </Message.Group>
                <Message.Group
                    align="end"
                >
                    <Message>
                        <Bubble
                            variant="user"
                        >
                            {t("messagePage.ownOne")}
                        </Bubble>
                    </Message>
                    <Message
                        deliveryStatus="delivered"
                        timestamp={TODAY}
                    >
                        <Bubble
                            variant="user"
                        >
                            {t("messagePage.ownTwo")}
                        </Bubble>
                    </Message>
                </Message.Group>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("messagePage.kindsTitle")}
            id="kinds"
        >
            <Paragraph>
                {t("messagePage.kindsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Message
                    timestamp={TODAY}
                    align="end"
                >
                    <Bubble
                        variant="user"
                    >
                        {t("sample.reply")}
                    </Bubble>
                </Message>
                <Message
                    avatar={{
                        title: t("sample.name")
                    }}
                    title={t("sample.name")}
                    timestamp={TODAY}
                >
                    <Bubble
                        variant="plain"
                    >
                        {t("messagePage.plainSample")}
                    </Bubble>
                </Message>
                <Message>
                    <Bubble
                        variant="system"
                    >
                        {t("messagePage.systemSample")}
                    </Bubble>
                </Message>
            </Preview>
        </Section>
        <Section
            title={t("messagePage.rowsTitle")}
            id="rows"
        >
            <Paragraph>
                {t("messagePage.rowsText")}
            </Paragraph>
            <Preview
                caption={t("messagePage.rowsCaption")}
                isStacked={true}
            >
                <Message
                    avatar={{
                        title: t("sample.name")
                    }}
                    title={t("sample.name")}
                    timestamp={TODAY}
                >
                    <Message.Row>
                        <Bubble>
                            {t("messagePage.rowsAnswer")}
                        </Bubble>
                    </Message.Row>
                    <Message.Row
                        isShowActions={false}
                    >
                        <CodeViewer
                            language="typescript"
                            code={ROWS_FIX}
                        />
                    </Message.Row>
                    <Message.Row
                        isShowActions={false}
                    >
                        <Trace>
                            <Trace.Line
                                description={t("messagePage.rowsStepReadMeta")}
                                title={t("messagePage.rowsStepRead")}
                                isConnected={true}
                            />
                            <Trace.Line
                                description={t("messagePage.rowsStepTestsMeta")}
                                title={t("messagePage.rowsStepTests")}
                                status="running"
                            />
                        </Trace>
                    </Message.Row>
                </Message>
                <Message
                    avatar={{
                        title: t("sample.name")
                    }}
                    title={t("sample.name")}
                    timestamp={TODAY}
                >
                    <Message.Row>
                        {
                            isBlockedRetrying ?
                                <Bubble>
                                    <Bubble.Typing />
                                </Bubble>
                            :
                                <Bubble
                                    variant="danger"
                                >
                                    {t("messagePage.rowsBlocked")}
                                </Bubble>
                        }
                    </Message.Row>
                    <Message.Row
                        isShowActions={false}
                    >
                        <Button
                            title={t("messagePage.rowsRetry")}
                            isDisabled={isBlockedRetrying}
                            onPress={handleBlockedRetry}
                            variant="link"
                            type="neutral"
                            size="small"
                        />
                    </Message.Row>
                </Message>
            </Preview>
            <CodeBlock
                code={ROWS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("messagePage.statusTitle")}
            id="status"
        >
            <Paragraph>
                {t("messagePage.timeText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Message
                    timestamp={TODAY}
                    align="end"
                >
                    <Bubble
                        variant="user"
                    >
                        {t("messagePage.timeToday")}
                    </Bubble>
                </Message>
                <Message
                    timestamp={YESTERDAY}
                    align="end"
                >
                    <Bubble
                        variant="user"
                    >
                        {t("messagePage.timeYesterday")}
                    </Bubble>
                </Message>
                <Message
                    timestamp={EARLIER}
                    align="end"
                >
                    <Bubble
                        variant="user"
                    >
                        {t("messagePage.timeEarlier")}
                    </Bubble>
                </Message>
            </Preview>
            <Paragraph>
                {t("messagePage.statusText")}
            </Paragraph>
            <Preview
                caption={t("messagePage.statusCaption")}
                isStacked={true}
            >
                {
                    STATUSES.map((status) => <Message
                        deliveryStatus={status}
                        timestamp={TODAY}
                        key={status}
                        align="end"
                    >
                        <Bubble
                            variant="user"
                            size="small"
                        >
                            {status}
                        </Bubble>
                    </Message>)
                }
                <Message
                    deliveryStatus={RETRY_FLOW[retryIndex]}
                    onRetry={handleRetry}
                    timestamp={TODAY}
                    align="end"
                >
                    <Bubble
                        variant="user"
                        size="small"
                    >
                        {RETRY_FLOW[retryIndex]}
                    </Bubble>
                </Message>
            </Preview>
        </Section>
        <Section
            title={t("messagePage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("messagePage.actionsText")}
            </Paragraph>
            <Preview
                caption={t("messagePage.actionsCaption")}
                isStacked={true}
            >
                <ToggleGroup
                    onValueChange={handleBehaviourChange}
                    title={t("messagePage.behaviour")}
                    attachBehaviour="attached"
                    isClearEnabled={false}
                    variant="outline"
                    value={behaviour}
                    size="small"
                >
                    {
                        BEHAVIOURS.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <Message
                    actionsBehaviour={behaviour}
                    avatar={{
                        title: t("sample.name")
                    }}
                    title={t("sample.name")}
                    timestamp={TODAY}
                >
                    <Bubble>
                        {t("sample.message")}
                    </Bubble>
                    {renderActions()}
                </Message>
            </Preview>
            <CodeBlock
                code={ACTIONS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("messagePage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <Message
                        avatar={{
                            title: t("sample.name")
                        }}
                        title={t("sample.name")}
                        size={size}
                        key={size}
                    >
                        <Bubble
                            size={size}
                        >
                            {size}
                        </Bubble>
                    </Message>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("messagePage.a11yArticle"),
                    t("messagePage.a11yFocus"),
                    t("messagePage.a11yGroup"),
                    t("messagePage.a11yToolbar"),
                    t("messagePage.a11yTouch"),
                    t("messagePage.a11yTime"),
                    t("messagePage.a11yStatus"),
                    t("messagePage.a11yAvatar"),
                    t("messagePage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("messagePage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("messagePage.propAlign"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("messagePage.propTimestamp"),
                        name: "timestamp",
                        type: "Date"
                    },
                    {
                        type: "\"sending\" | \"queued\" | \"sent\" | \"delivered\" | \"read\" | \"failed\"",
                        description: t("messagePage.propDeliveryStatus"),
                        name: "deliveryStatus"
                    },
                    {
                        description: t("messagePage.propOnRetry"),
                        type: "() => void",
                        name: "onRetry"
                    },
                    {
                        description: t("messagePage.propAvatar"),
                        type: "Omit<IAvatarProps, \"size\">",
                        name: "avatar"
                    },
                    {
                        description: t("messagePage.propIsAvatarVisible"),
                        name: "isAvatarVisible",
                        type: "boolean"
                    },
                    {
                        type: "\"first\" | \"middle\" | \"last\" | \"single\"",
                        description: t("messagePage.propGroupPosition"),
                        defaultValue: "\"single\"",
                        name: "groupPosition"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("messagePage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("messagePage.propActionsBehaviour"),
                        type: "\"auto\" | \"always\" | \"none\"",
                        defaultValue: "\"auto\"",
                        name: "actionsBehaviour"
                    },
                    {
                        description: t("messagePage.propIsLatest"),
                        defaultValue: "false",
                        name: "isLatest",
                        type: "boolean"
                    },
                    {
                        description: t("messagePage.propIsStreaming"),
                        defaultValue: "false",
                        name: "isStreaming",
                        type: "boolean"
                    },
                    {
                        description: t("messagePage.propOnLongPress"),
                        name: "onLongPress",
                        type: "() => void"
                    },
                    {
                        description: t("messagePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("messagePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("messagePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("messagePage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Message.Group
                </InlineCode>
                {" "}
                {t("messagePage.groupPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messagePage.groupAlign"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("messagePage.groupAvatar"),
                        type: "Omit<IAvatarProps, \"size\">",
                        name: "avatar"
                    },
                    {
                        description: t("messagePage.groupTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("messagePage.groupChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Message.Header
                </InlineCode>
                {" "}
                {t("messagePage.headerPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messagePage.headerBadge"),
                        type: "string",
                        name: "badge"
                    },
                    {
                        description: t("messagePage.headerIsTimeVisible"),
                        name: "isTimeVisible",
                        defaultValue: "true",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Message.Actions
                </InlineCode>
                {" "}
                {t("messagePage.actionsPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messagePage.actionsIsVisible"),
                        name: "isVisible",
                        type: "boolean"
                    },
                    {
                        description: t("messagePage.actionsTitleProp"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("messagePage.actionsChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("messagePage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MessagePage;
